import type { ReactNode } from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { MobileNav } from './MobileNav';

type AppShellProps = {
  children: ReactNode;
};

/**
 * Wrapper layout for every in-app route. Provides the page chrome
 * (header + footer + mobile nav) and the page background.
 *
 * The background + body text are the ONLY theme-aware surfaces here —
 * the nav stays in the night-* palette regardless of the toggle, per
 * the product decision to keep nav identity constant across themes.
 * Body components (cards, lists, etc.) supply their own classes.
 */
export function AppShell({ children }: AppShellProps) {
  return (
    <div className="flex min-h-dvh flex-col bg-ink-50 text-ink-900 dark:bg-night-900 dark:text-night-50">
      <a
        href="#main"
        className="
          sr-only focus:not-sr-only
          focus:fixed focus:left-3 focus:top-3 focus:z-50
          focus:rounded-md focus:bg-beam focus:px-3 focus:py-2
          focus:text-sm focus:font-semibold focus:text-beam-ink focus:shadow-sm
        "
      >
        Skip to main content
      </a>
      <Header />
      {/* pb-24 reserves space for the MobileNav (~68px + safe-area) on
          mobile and tablet; nav is hidden ≥lg so padding drops to 0. */}
      <main id="main" tabIndex={-1} className="flex-1 pb-24 lg:pb-0">
        {children}
      </main>
      <Footer />
      <MobileNav />
    </div>
  );
}
